import {
  GrowthBook,
  LocalStorageStickyBucketService,
  configureCache,
  setPolyfills,
  type LocalStorageCompat,
  type TrackingCallback,
} from '@growthbook/growthbook';
import { growthbookTrackingPlugin } from '@growthbook/growthbook/plugins';
import {
  NEXT_PUBLIC_GROWTHBOOK_DEV_MODE,
  NEXT_PUBLIC_GROWTHBOOK_DISABLED,
  NEXT_PUBLIC_GROWTHBOOK_INGESTOR_HOST,
} from '@/lib/env';
import { getConsent } from '@/lib/consent';
import { clearGrowthBookClientStorage, markGrowthBookCleanupStale } from './cleanup';
import { GROWTHBOOK_STICKY_BUCKET_PREFIX } from './constants';
import { sendAnonymousExperimentViewed } from './ingestion';

export const isGrowthBookConfigured = (apiHost?: string, clientKey?: string): boolean =>
  !NEXT_PUBLIC_GROWTHBOOK_DISABLED && Boolean(apiHost) && Boolean(clientKey);

/** SDK feature cache: persist to localStorage only when consent allows stored identifiers. */
const applyFeatureCachePersistence = (persist: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (persist) {
    try {
      setPolyfills({ localStorage: window.localStorage });
      configureCache({ disableCache: false });
      return;
    } catch {
      // Fall through to disable caching when browser storage is blocked.
    }
  }

  setPolyfills({ localStorage: undefined });
  configureCache({ disableCache: true });
};

const withConsentAllowedLocalStorage = <T>(
  callback: (localStorage: Storage) => T,
  fallback: T
): T => {
  if (typeof window === 'undefined' || !getConsent().allowed) {
    return fallback;
  }

  try {
    return callback(window.localStorage);
  } catch {
    return fallback;
  }
};

const consentAwareLocalStorage: LocalStorageCompat = {
  getItem: (key) =>
    withConsentAllowedLocalStorage((localStorage) => localStorage.getItem(key), null),
  setItem: (key, value) =>
    withConsentAllowedLocalStorage((localStorage) => localStorage.setItem(key, value), undefined),
};

/** Product callback plus consent-off anonymous exposure. Plugin handles consent-on ingest. */
const bindSessionTrackingCallback = (
  instance: GrowthBook,
  productTrackingCallback: TrackingCallback
): void => {
  instance.setTrackingCallback((experiment, result) => {
    productTrackingCallback(experiment, result);
    if (!getConsent().allowed) {
      sendAnonymousExperimentViewed(instance, experiment.key, result.key);
    }
  });
};

/** Bare instance used when GrowthBook is kill-switched via env. */
export const createDisabledGrowthBook = (): GrowthBook => new GrowthBook({});

/** One session instance; consent only toggles persistence and tracked identity, not GrowthBook itself. */
export const createSessionGrowthBook = ({
  apiHost,
  clientKey,
  trackingCallback,
}: {
  apiHost: string;
  clientKey: string;
  trackingCallback: TrackingCallback;
}): GrowthBook => {
  applyFeatureCachePersistence(false);

  const instance = new GrowthBook({
    apiHost,
    clientKey,
    enabled: true,
    // Lets the GrowthBook DevTools browser extension detect the SDK and force variations
    enableDevMode: NEXT_PUBLIC_GROWTHBOOK_DEV_MODE,
    disableExperimentsOnLoad: false,
    stickyBucketService: new LocalStorageStickyBucketService({
      prefix: GROWTHBOOK_STICKY_BUCKET_PREFIX,
      localStorage: consentAwareLocalStorage,
    }),
    plugins: [
      growthbookTrackingPlugin({
        ingestorHost: NEXT_PUBLIC_GROWTHBOOK_INGESTOR_HOST,
        eventFilter: () => getConsent().allowed,
      }),
    ],
  });

  bindSessionTrackingCallback(instance, trackingCallback);
  return instance;
};

/** Consent granted: allow GrowthBook to persist SDK cache and use full tracking attributes. */
export const restoreGrowthBookExperiments = (
  instance: GrowthBook | null | undefined,
  trackingCallback: TrackingCallback
): void => {
  markGrowthBookCleanupStale();

  if (!instance || instance.isDestroyed()) {
    return;
  }

  applyFeatureCachePersistence(true);
  bindSessionTrackingCallback(instance, trackingCallback);
};

/** Consent not accepted: keep GrowthBook running, but clear/disable persistence. */
export const withdrawGrowthBookConsent = (
  instance: GrowthBook | null | undefined,
  trackingCallback: TrackingCallback
): void => {
  applyFeatureCachePersistence(false);
  clearGrowthBookClientStorage();

  if (!instance || instance.isDestroyed()) {
    return;
  }

  bindSessionTrackingCallback(instance, trackingCallback);
};
